// src/features/MyGymTV/UK/DSDetails/DSDetails.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../../layout/Layout';
import { Card, Form, Button, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSave, faArrowLeft, faImage, faEye } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import UKDatePicker from '../../UK/UKDatePicker/UKDatePicker';
import './ds-details.scss';

// Module-level cache - shared with other components
const mediaCache = new Map();

const DSDetails = () => {
    const navigate = useNavigate();
    const { orderContentId } = useParams();
    const { selectedClubs } = useSelector((state) => state.ukEntities);
    const allAccessibleClubs = useSelector((state) => state.user.ukEntityData || []);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [contentDetails, setContentDetails] = useState(null);
    const [mediaUrl, setMediaUrl] = useState(null);
    const [mediaType, setMediaType] = useState(null);
    const [mediaLoading, setMediaLoading] = useState(false);
    const videoRef = useRef(null);
    const [orderId, setOrderId] = useState(null);
    const [contentId, setContentId] = useState(null);

    const [fileName, setFilename] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isActive, setIsActive] = useState(true);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [affectedVenues, setAffectedVenues] = useState([]);

    const [originalValues, setOriginalValues] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    const [showUpdateVenuesModal, setShowUpdateVenuesModal] = useState(false);
    const [allVenues, setAllVenues] = useState([]);
    const [selectedVenueIds, setSelectedVenueIds] = useState([]);

    const handleReset = () => {
        if (!originalValues) return;

        setStartDate(originalValues.startDate);
        setEndDate(originalValues.endDate);
        setIsActive(originalValues.isActive);
        setError(null);
        setSuccessMessage('Form reset to original values');
    };

    const handleManageVenues = () => {
        const currentVenueIds = contentDetails.map(item => item.VenueID);
        setSelectedVenueIds(currentVenueIds);
        setAllVenues(allAccessibleClubs);
        setShowUpdateVenuesModal(true);
    };

    const handleUpdateVenues = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem('token');

            await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/UpdateDSApprovals`,
                {
                    orderId: orderId,
                    clubIds: selectedVenueIds
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setShowUpdateVenuesModal(false);

            if (selectedVenueIds.length === 0) {
                setSuccessMessage('Content removed from all venues');
                setTimeout(() => {
                    navigate('/uk/review-ds');
                }, 1500);
            } else {
                setSuccessMessage('Venue assignments updated successfully');
                fetchContentDetails();
            }

        } catch (err) {
            console.error('Error updating venues:', err);
            setError('Failed to update venue assignments');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        fetchContentDetails();
    }, [orderContentId]);

    const fetchContentDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/GetDSContentDetails`,
                { orderContentId: parseInt(orderContentId) },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data && response.data.length > 0) {
                const content = response.data[0];
                const startDateObj = content.StartDate ? moment(content.StartDate).toDate() : null;
                const endDateMoment = content.EndDate ? moment(content.EndDate) : null;
                const endDateObj = (endDateMoment && endDateMoment.year() >= 2100)
                    ? null
                    : endDateMoment?.toDate();

                setContentDetails(response.data);
                setFilename(content.Filename);
                setOrderId(content.OrderID);
                setContentId(content.ContentID);
                setStartDate(startDateObj);
                setEndDate(endDateObj);
                setIsActive(content.IsActive);

                setOriginalValues({
                    startDate: startDateObj,
                    endDate: endDateObj,
                    isActive: content.IsActive
                });

                // Check cache for media
                if (mediaCache.has(content.ContentID)) {
                    const cached = mediaCache.get(content.ContentID);
                    setMediaUrl(cached.url);
                    setMediaType(cached.type);
                }
                // Don't auto-fetch - let user click to load

            } else {
                setError('Content not found');
            }

        } catch (err) {
            console.error('Error fetching content details:', err);
            setError('Failed to load content details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!originalValues) return;

        const changed =
            (startDate?.getTime() !== originalValues.startDate?.getTime()) ||
            (endDate?.getTime() !== originalValues.endDate?.getTime()) ||
            isActive !== originalValues.isActive;

        setHasChanges(changed);
    }, [startDate, endDate, isActive, originalValues]);

    const handleLoadPreview = async () => {
        if (!contentId || mediaLoading) return;

        // Check cache first
        if (mediaCache.has(contentId)) {
            const cached = mediaCache.get(contentId);
            setMediaUrl(cached.url);
            setMediaType(cached.type);
            return;
        }

        try {
            setMediaLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/DigitalSignage/${contentId}/content`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            // Store in cache
            mediaCache.set(contentId, { url, type: blob.type });

            setMediaUrl(url);
            setMediaType(blob.type);
        } catch (error) {
            console.error('Error fetching media:', error);
            setError('Failed to load preview');
        } finally {
            setMediaLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        };
    }, []);

    const handleSave = () => {
        const venues = contentDetails.map(item => {
            const club = allAccessibleClubs.find(c => c.ClubId === item.VenueID);
            return {
                VenueID: item.VenueID,
                VenueName: club?.ClubName || `Venue ${item.VenueID}`
            };
        });

        setAffectedVenues(venues);
        setShowConfirmModal(true);
    };

    const confirmSave = async () => {
        try {
            setSaving(true);
            setShowConfirmModal(false);
            const token = localStorage.getItem('token');

            const updateData = {
                orderId: orderId,
                orderContentId: parseInt(orderContentId),
                startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
                endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
                isActive: isActive,
                displaySeconds: 10
            };

            await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/SaveDSContentDetails`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setSuccessMessage('Content updated successfully');

            setOriginalValues({
                startDate: startDate,
                endDate: endDate,
                isActive: isActive
            });

            setHasChanges(false);

        } catch (err) {
            console.error('Error updating content:', err);
            setError('Failed to update content');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="ds-details-container mt-5 text-center">
                    <FontAwesomeIcon
icon={faSpinner}
size="3x"
spin
                    />
                    <p className="mt-3">Loading content...</p>
                </div>
            </Layout>
        );
    }

    if (error && !contentDetails) {
        return (
            <Layout>
                <div className="ds-details-container mt-5">
                    <Alert variant="danger">{error}</Alert>
                    <Button onClick={() => navigate('/uk/review-ds')}>
                        <FontAwesomeIcon
className="me-2"
icon={faArrowLeft}
                        />
                        Back to Review
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="ds-details-container mt-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>Edit DS Content</h4>
                    <Button
                        onClick={() => navigate('/uk/review-ds')}
                        variant="secondary"
                    >
                        <FontAwesomeIcon
className="me-2"
icon={faArrowLeft}
                        />
                        Back to Review
                    </Button>
                </div>

                {successMessage && (
                    <Alert
                        dismissible
                        onClose={() => setSuccessMessage(null)}
                        variant="success"
                    >
                        {successMessage}
                    </Alert>
                )}

                {error && (
                    <Alert
                        dismissible
                        onClose={() => setError(null)}
                        variant="danger"
                    >
                        {error}
                    </Alert>
                )}

                <Card>
                    <Card.Body>
                        <div className="row">
                            <div className="col-md-6">
                                <h5 className="mb-3">Content Preview</h5>
                                {mediaUrl ? (
                                    <>
                                        {mediaType && mediaType.startsWith('video/') ? (
                                            <video
                                                className="rounded"
                                                controls
                                                loop
                                                muted
                                                ref={videoRef}
                                                style={{ width: '100%', height: 'auto' }}
                                            >
                                                <source
src={mediaUrl}
type={mediaType}
                                                />
                                                Your browser does not support the video tag.
                                            </video>
                                        ) : (
                                            <img
                                                alt="Content"
                                                className="img-fluid rounded"
                                                src={mediaUrl}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <div
                                        className="preview-placeholder rounded"
                                        onClick={handleLoadPreview}
                                        onKeyDown={(e) => e.key === 'Enter' && handleLoadPreview()}
                                        role="button"
                                        style={{
                                            background: 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)',
                                            border: '2px dashed #adb5bd',
                                            cursor: mediaLoading ? 'wait' : 'pointer',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            aspectRatio: '16 / 9',
                                            maxHeight: '300px',
                                            transition: 'all 0.2s ease'
                                        }}
                                        tabIndex={0}
                                    >
                                        {mediaLoading ? (
                                            <>
                                                <FontAwesomeIcon
icon={faSpinner}
size="2x"
spin
                                                />
                                                <span className="mt-2">Loading preview...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon
className="text-muted mb-2"
icon={faImage}
size="3x"
                                                />
                                                <span className="text-muted">
                                                    <FontAwesomeIcon
className="me-1"
icon={faEye}
                                                    />
                                                    Click to load preview
                                                </span>
                                            </>
                                        )}
                                    </div>
                                )}
                                <div className="mt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <strong>Assigned Venues:</strong>
                                        <Button
                                            onClick={handleManageVenues}
                                            size="sm"
                                            variant="outline-primary"
                                        >
                                            Manage Venues
                                        </Button>
                                    </div>
                                    <div className="venue-list mt-2">
                                        {contentDetails?.map(item => {
                                            const club = allAccessibleClubs.find(c => c.ClubId === item.VenueID);
                                            return (
                                                <div
className="py-1"
key={item.VenueID}
                                                >
                                                    {club?.ClubName || `Venue ${item.VenueID}`}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <h5 className="mb-3">Content Details</h5>
                                <div>Filename: {fileName}</div>
                                <Form>
                                    <UKDatePicker
                                        label="Start Date"
                                        minDate={new Date()}
                                        onChange={(date) => setStartDate(date)}
                                        selected={startDate}
                                    />

                                    <UKDatePicker
                                        label="End Date (Optional)"
                                        minDate={startDate || new Date()}
                                        onChange={(date) => setEndDate(date)}
                                        placeholder="No end date (∞)"
                                        selected={endDate}
                                        showClearButton
                                    />
                                    {!endDate && (
                                        <Form.Text className="text-muted d-block mb-3">
                                            No end date set (∞)
                                        </Form.Text>
                                    )}

                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            checked={isActive}
                                            label="Active"
                                            onChange={(e) => setIsActive(e.target.checked)}
                                            type="checkbox"
                                        />
                                    </Form.Group>

                                    <div className="d-flex gap-2">
                                        <Button
                                            disabled={saving || !hasChanges}
                                            onClick={handleSave}
                                            variant="primary"
                                        >
                                            <FontAwesomeIcon
className="me-2"
icon={faSave}
                                            />
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </Button>

                                        <Button
                                            disabled={saving || !hasChanges}
                                            onClick={handleReset}
                                            variant="secondary"
                                        >
                                            Reset
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Save Confirmation Modal */}
                <Modal
                    onHide={() => setShowConfirmModal(false)}
                    show={showConfirmModal}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Save</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>This action will update this content for the following venue(s):</p>
                        <ul>
                            {affectedVenues.map(venue => (
                                <li key={venue.VenueID}>{venue.VenueName}</li>
                            ))}
                        </ul>
                        <p className="mb-0">Are you sure you want to save these changes?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            onClick={() => setShowConfirmModal(false)}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmSave}
                            variant="primary"
                        >
                            Save Changes
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Manage Venues Modal */}
                <Modal
                    onHide={() => setShowUpdateVenuesModal(false)}
                    show={showUpdateVenuesModal}
                    size="lg"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Manage Venue Assignments</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="info">
                            Select which venues should display this content. Unchecking all venues will remove this content entirely.
                        </Alert>
                        <div className="venue-checkboxes">
                            {allVenues.map(venue => (
                                <Form.Check
                                    checked={selectedVenueIds.includes(venue.ClubId)}
                                    className="mb-2"
                                    id={`venue-${venue.ClubId}`}
                                    key={venue.ClubId}
                                    label={venue.ClubName}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedVenueIds([...selectedVenueIds, venue.ClubId]);
                                        } else {
                                            setSelectedVenueIds(selectedVenueIds.filter(id => id !== venue.ClubId));
                                        }
                                    }}
                                    type="checkbox"
                                />
                            ))}
                        </div>
                        {selectedVenueIds.length === 0 && (
                            <Alert
className="mt-3"
variant="warning"
                            >
                                <strong>Warning:</strong> Removing from all venues will delete this content.
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            onClick={() => setShowUpdateVenuesModal(false)}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={saving}
                            onClick={handleUpdateVenues}
                            variant="primary"
                        >
                            {saving ? 'Updating...' : `Update (${selectedVenueIds.length} venues)`}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Layout>
    );
};

export default DSDetails;